import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Theme,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';

import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import Loader from '../../../components/Loader';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import FormDateField from '../../../components/formFields/FormDateField';
import { NET_INTENT_TYPE_OPTIONS, NET_PAYMENT_METHOD_OPTIONS } from '../../../constants/constants';
import { netSettlementEditFormSchema } from '../../../helpers/formSchemas/formSchemas';
import { NET_SETTLEMENT_EDIT_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import { useEffect, useState } from 'react';
import { NetSettlementEditFormSubmitProps } from '../types';
import { updateNetSettlementConfig } from '../../../api/paymentsApi/settlementsApi';
import useSnackBar from '../../../hooks/useSnackBar';

interface EditNetSettlementPopupProps {
    isOpen: boolean;
    loading?: boolean;
    onSuccess?: () => void;
    onFailed?: () => void;
    onCancel: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialData?: any;
    settlementId: number;
    canEdit?: boolean;
}
const useStyles = makeStyles()((theme: Theme) => ({
    popupContainer: {
        '& .MuiDialog-paper': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.light,
            maxWidth: '550px',
            width: '100%',
            padding: '32px',
            boxSizing: 'border-box',
            borderRadius: '16px',
            maxHeight: '95%',
        },

        '& .MuiDialogTitle-root': {
            padding: 0,
            fontSize: '34px',
            fontWeight: 600,
            lineHeight: '120%',
            color: theme.palette.common.white,
            fontFamily: 'Poppins',
        },

        '& .MuiDialogContent-root': {
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            '&::-webkit-scrollbar': {
                width: '5px',
                borderRadius: '20px',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: 20,
                backgroundColor: theme.palette.secondary.dark,
            },
        },

        '& .MuiDialogContentText-root': {
            color: theme.palette.secondary.main,
        },

    },
    popupActions: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '416px',
        width: '100%',
        margin: '0 auto',
        gap: '16px',
        padding: 0,
    },
    popupButton: {
        color: theme.palette.info.main,

        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
        },

        '&:hover': {
            background: 'rgba(144, 202, 249, 0.08)',
        },
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '416px',
        width: '100%',
    },
    formFieldsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        alignItems: 'center',
        padding: '32px 0 32px 0',
    },
    checkboxWrapper: {
        width: '100%',

        '& label': {
            marginRight: 0,
        },
    },
}));


const EditNetSettlementPopup = ({
    isOpen,
    loading,
    onCancel,
    initialData,
    settlementId,
    onSuccess,
    canEdit,
}: EditNetSettlementPopupProps) => {
    const { classes } = useStyles();
    const { setAlert } = useSnackBar();
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    const handleClose = () => {
        reset({
            ...NET_SETTLEMENT_EDIT_FORM_DEFAULT_VALUES,
            ...initialData,
        });
        onCancel();
    };

    const formMethods = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(netSettlementEditFormSchema),
        defaultValues: NET_SETTLEMENT_EDIT_FORM_DEFAULT_VALUES,
    });

    const {
        handleSubmit,
        watch,
        reset,
        formState: { isValid, isDirty },
    } = formMethods;

    // Watch paymentIntention to determine if EXTERNAL is selected
    const paymentIntention = watch('paymentIntention');
    const isExternal = paymentIntention === 'EXTERNAL';

    useEffect(() => {
        if (initialData) {
            reset({
                ...NET_SETTLEMENT_EDIT_FORM_DEFAULT_VALUES,
                ...initialData,
            });
        }
    }, [initialData, reset]);

    const onSubmitForm = (values: NetSettlementEditFormSubmitProps) => {
        setIsFormSubmitting(true);
        let payload;
        // Format externalPaymentDate as ISO string if present
        if (isExternal) {
            const formattedDate = values.externalPaymentDate instanceof Date
                ? values.externalPaymentDate.toISOString()
                : values.externalPaymentDate || '';
            payload = {
                netSettlement: {
                    paymentIntention: values.paymentIntention,
                    externalPaymentMethod: values.externalPaymentMethod,
                    externalPaymentReference: values.externalPaymentReference,
                    externalPaymentMemo: values.externalPaymentMemo,
                    externalPaymentDate: formattedDate,
                },
            };
        } else {
            payload = {
                netSettlement: {
                    paymentIntention: values.paymentIntention,
                },
            };
        }


        updateNetSettlementConfig(settlementId, payload)
            .then(() => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: 'Net settlement updated successfully',
                    type: 'success',
                }));
                onSuccess && onSuccess();
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((err: any) => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: err?.response?.data?.message || 'Failed to update net settlement',
                    type: 'error',
                }));
            });
    };

    return (
        <Dialog className={classes.popupContainer} open={isOpen} onClose={handleClose} scroll="paper" >
            <Box display="flex" justifyContent="flex-end">
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disableRipple
                    sx={(theme) => ({
                        color: theme.palette.common.white,
                    })}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogTitle>
                {'Edit Net Settlement'}
            </DialogTitle>
            {loading ? (
                <Box sx={{ margin: '30px' }}>
                    <Loader colorType={'warning'} />
                </Box>
            ) : (
                <>
                    <DialogContent>
                        <FormProvider {...formMethods}>
                            <Box className={classes.formContainer}>
                                <Box className={classes.formFieldsWrapper}>
                                    {/* 🟦 Deduction Section */}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 600,
                                            alignSelf: 'flex-start',
                                            marginBottom: '8px',
                                            fontSize: '18px',
                                        }}
                                    >
                                        Payment
                                    </Typography>

                                    <FormSelectField
                                        name="paymentIntention"
                                        label="Intent"
                                        options={NET_INTENT_TYPE_OPTIONS}
                                        width="100%"
                                        disabled={!canEdit}
                                    />

                                    {paymentIntention !== 'PAY' && (<>
                                        <FormSelectField
                                            name="externalPaymentMethod"
                                            label={`Payment Method${isExternal ? ' *' : ''}`}
                                            options={NET_PAYMENT_METHOD_OPTIONS}
                                            width="100%"
                                            disabled={!canEdit}
                                        />
                                        <FormTextField
                                            name="externalPaymentReference"
                                            label={`Payment Reference${isExternal ? ' *' : ''}`}
                                            disabled={!canEdit}
                                        />
                                        <FormTextField
                                            name='externalPaymentMemo'
                                            label='Memo'
                                            isMultiline
                                            isDescription
                                            disabled={!canEdit}
                                        />
                                        <FormDateField
                                            name="externalPaymentDate"
                                            label={`Payment Date${isExternal ? ' *' : ''}`}
                                            maxDate={moment()}
                                            disabled={!canEdit}
                                        />
                                    </>)}
                                </Box>

                                <DialogActions className={classes.popupActions}>
                                    {canEdit && (
                                        <>
                                            <FormSubmitButton
                                                disabled={!isDirty || !isValid || isFormSubmitting}
                                                buttonText="Submit"
                                                onClick={handleSubmit(onSubmitForm)}
                                            />
                                            <FormCancelButton onClick={handleClose} />
                                        </>
                                    )}
                                </DialogActions>
                            </Box>
                        </FormProvider>
                    </DialogContent>

                </>
            )}
        </Dialog>
    );
};

export default EditNetSettlementPopup;